
import appCan from 'bits/app_can'
import { isAppUsing } from 'bits/flip'
import { PiniaMapState } from 'bits/pinia'
import { Action, Component, Getter, namespace, Prop, State, Vue } from 'bits/vue'
import { useReactionsStore } from 'pinia/surface_reactions'
import type { Cid, Id, Post, User, WallReactionData } from 'types'
import { defineAsyncComponent } from 'vue'
import SurfacePostCommentsIcon from 'vuecomponents/SurfacePostCommentsIcon.vue'
@Component({
  components: {
    SurfacePostReactionOld: defineAsyncComponent(() => import('vuecomponents/surface-post-reaction-new.vue')),
    SurfacePostReaction: defineAsyncComponent(() => import('vuecomponents/SurfacePostReaction.vue')),
    SurfacePostCommentsIcon,
  },
})
export default class SurfacePostSocialContentTopBar extends Vue {
  $refs!: {
    socialContentTopBar: HTMLDivElement
  }

  isAppUsing = isAppUsing

  @Prop({ required: true }) post!: Post & { id: Id }
  @Prop({ default: 'postView' }) usageContext!: 'postView' | 'postExpandedView'
  @Prop({ default: () => [] }) postReactionIds
  @Prop({ default: () => [] }) postCommentIds

  @Getter('colorScheme') wallColorScheme!: 'light' | 'dark'
  @namespace('browser').State darkMode!: boolean
  @namespace('browser').Getter isPhone!: boolean
  @namespace('postAction').Action expandPost!: (payload: {
    postCid: Cid
    xTextPanel?: boolean
    shouldFocusInput?: boolean
  }) => void

  get isUsedInPostExpandedView(): boolean {
    return this.usageContext === 'postExpandedView'
  }

  get colorScheme(): 'light' | 'dark' {
    if (this.isUsedInPostExpandedView) {
      return this.darkMode ? 'dark' : 'light'
    }
    return this.wallColorScheme
  }

  /**
   * REACTIONS
   */
  @Getter isReactable!: boolean
  @Getter reactionData!: WallReactionData
  @PiniaMapState(useReactionsStore) accumulatedReactionsByWishId
  @State user!: User
  @namespace('reaction').State postIdBeingReactedTo!: Id
  @namespace('reaction').Action startEditingReaction!: (payload: {
    postId: Id
    dropdownLeft?: number
    dropdownWidth?: number
    dropdownTop?: number
  }) => void

  get xReactions(): boolean {
    // when a user is viewing a wall with reaction disabled and then reaction is enabled + someone react to something
    // the realtime message for new reaction may come before the reaction enable message
    // => wait for reactionData to be available before showing reactions
    const reactionCount = this.accumulatedReactionsByWishId[this.post.id]?.totalReactionsCount
    return (this.isReactable || this.postReactionIds.length > 0 || reactionCount > 0) && !!this.reactionData
  }

  get isBeingReacted(): boolean {
    return this.postIdBeingReactedTo === this.post.id
  }

  editReaction(): void {
    if (this.isPhone) {
      this.startEditingReaction({ postId: this.post.id })
      return
    }
    if (this.$el.closest('.surface-post')?.classList.contains('noclick')) {
      // Prevent reaction event when the post is being dragged
      return
    }

    const { left, top, width } = this.$refs.socialContentTopBar.getBoundingClientRect()

    this.startEditingReaction({
      postId: this.post.id,
      dropdownLeft: left,
      dropdownTop: top,
      dropdownWidth: width,
    })
  }

  /**
   * COMMENTS
   */
  @Getter xPostCommentsInPanel!: boolean
  @Getter isCommentable!: boolean
  @Action showCommentsPanel!: (payload: { postId: Id }) => void

  get xComments(): boolean {
    // Turn comments off when switching to Backchannel
    return this.post.published === true && (this.isCommentable || this.postCommentIds.length > 0)
  }

  showComments(): void {
    if (this.isUsedInPostExpandedView) return
    if (!appCan('useNativeCommentsPanel')) {
      this.expandPost({ postCid: this.post.cid, xTextPanel: true, shouldFocusInput: !this.isPhone })
    } else {
      // native app should still show the old comments panel
      this.showCommentsPanel({ postId: this.post.id })
    }
  }
}
